import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { type Lang, type TranslationKey, translate } from "./i18n-dict";

const STORAGE_KEY = "knode.lang";

interface I18nValue {
  lang: Lang;
  setLang: (next: Lang) => void;
  /**
   * Render `key` in the active language. `vars` is interpolated against
   * `{name}` placeholders. Use the `TranslationKey` autocomplete — typing
   * a key that no dict declares is a compile error.
   */
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

/**
 * Pick the initial language. Order:
 *   1. Whatever the user previously chose (localStorage)
 *   2. Browser preference if it starts with `zh`
 *   3. English
 *
 * SSR-safe: returns `"en"` on the server. The provider re-runs this on
 * mount so the client takes over once the user's preference is known.
 */
function detectDefault(): Lang {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Lang | null;
    if (stored === "en" || stored === "zh") return stored;
  } catch {
    // localStorage can throw in private mode / sandboxed iframes.
  }
  const nav = typeof navigator !== "undefined" ? (navigator.language ?? "") : "";
  return nav.toLowerCase().startsWith("zh") ? "zh" : "en";
}

/**
 * Provides language state to the React tree. Mount this **once**, near
 * the root (we mount it in `app/layout.tsx`).
 *
 * Why client-side only: knode-os pages are heavy SSR/RSC, but the
 * language preference is a per-user-per-device thing that must not
 * leak across users via the cache. Storing it in localStorage and
 * reading it on mount keeps the SSR HTML cacheable / deterministic
 * and lets the client swap labels in immediately on hydration.
 */
export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
  const [lang, setLangState] = useState<Lang>("en");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setLangState(detectDefault());
    setHydrated(true);
  }, []);

  // Reflect the choice on the <html> element so screen readers and
  // browser features (translate, hyphenation) honour it.
  useEffect(() => {
    if (!hydrated) return;
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  }, [lang, hydrated]);

  const setLang = useCallback((next: Lang) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore — we still update the in-memory state.
    }
    setLangState(next);
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      setLang,
      t: (key, vars) => translate(lang, key, vars),
    }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

/**
 * Convenience hook when a component only needs `t`. Same instance as
 * `useI18n().t` — separate hook keeps the call sites short.
 */
export function useT(): I18nValue["t"] {
  return useI18n().t;
}
